import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaBuilding, FaTruck, FaRecycle, FaCheck, FaUsers, FaChevronDown, FaChevronUp, FaStar, FaHandHoldingHeart, FaStore } from 'react-icons/fa';
import './Pricing.css';

const DURATION_OPTIONS = [
  { id: '3months', label: '3 Months', months: 3, discount: 10, popular: false },
  { id: '6months', label: '6 Months', months: 6, discount: 15, popular: false },
  { id: '12months', label: '12 Months', months: 12, discount: 30, popular: true }
];

const PLANS = [
  {
    id: 'authority',
    name: 'Public Authority',
    subtitle: 'Local Councils & Public Bodies',
    icon: FaBuilding,
    basePrice: 1000,
    headlineFeatures: [
      'Free waste reporting and map visuals by citizens'
    ],
    generalBenefits: {
      intro: 'Show residents you are:',
      items: [
        'Taking their health and environment seriously',
        'Building stronger council-citizen relationship',
        'Responding to citizen waste complaints',
        'Modernising waste management',
        'Making data/evidence-based actions',
        'Turning critics into supporters'
      ]
    },
    dashboardFeatures: [
      'Territorial Map-based markers of waste',
      'Overview reported waste by period',
      'No of wastes reported per month',
      'Clean Up rate',
      'Instant Dashboard Alert on wastes',
      'Waste "Severity Scoring" System',
      'Prioritisation based on location or waste type',
      'Prioritisation of waste (Critical, High, Medium, Low)',
      'Priority Waste Queue',
      'Status of waste reported (Reported, Crew Assigned, Cleaned-Up)',
      'Waste response time trends',
      'Time-to-Clean up (by waste category)',
      'Top community waste reporters',
      'Weekly waste report trend',
      'Waste Crew Management',
      'Assignment Pipeline',
      'Export Waste Data (Excel and JSON)',
      'Generate Summary Reports (customised)',
      'Compliance & Ordinance Tracking',
      'SLA Analytics & Reports',
      'Issue Jobs to Private Companies',
      'Up to 5 System Administrators'
    ],
    color: '#3498db'
  },
  {
    id: 'private_company',
    name: 'Waste Management Company',
    subtitle: 'Private Waste Collection Services (Nationwide Access)',
    icon: FaTruck,
    basePrice: 600,
    headlineFeatures: [
      'Free access to wastes reported nationwide',
      'Free access to tasks/information by crews'
    ],
    generalBenefits: {
      intro: 'Transform your operations with:',
      items: [
        'Streamlined job intake from authorities',
        'Real-time visibility on waste locations',
        'Improved route efficiency and cost savings',
        'Professional invoicing and tracking',
        'Data-driven performance insights',
        'Competitive edge in tender bids'
      ]
    },
    dashboardFeatures: [
      'Territorial Map-based waste markers',
      'Overview of assigned waste by period',
      'Job Inbox & Acceptance System',
      'Active Jobs Tracking Dashboard',
      'Instant Alert on new job assignments',
      'Waste "Severity Scoring" System',
      'Prioritisation of waste (Critical, High, Medium, Low)',
      'Priority Job Queue',
      'Status tracking (Accepted, In Progress, Completed)',
      'Response time analytics',
      'Time-to-Complete metrics',
      'Performance Metrics Dashboard',
      'Cost Tracking & Reporting',
      'Invoice Management System',
      'Export Job Data (Excel and JSON)',
      'Generate Performance Reports',
      'Route Optimisation Insights',
      'Unlimited System Administrators'
    ],
    color: '#e67e22'
  },
  {
    id: 'recycling',
    name: 'Recycling Business',
    subtitle: 'Waste-to-Wealth Enterprises (Nationwide Access)',
    icon: FaRecycle,
    basePrice: 600,
    headlineFeatures: [
      'Free access to wastes reported nationwide',
      'Free access to tasks/information by crews'
    ],
    generalBenefits: {
      intro: 'Maximise your recycling potential:',
      items: [
        'First access to valuable recyclable materials',
        'Real-time waste location intelligence',
        'Material value estimation before collection',
        'Track environmental impact contribution',
        'Build reputation as sustainability partner',
        'Data-backed business growth decisions'
      ]
    },
    dashboardFeatures: [
      'Territorial Map-based recyclable markers',
      'Recyclable Waste Type Filter',
      'Overview of materials by period',
      'Material alerts by waste category',
      'Material Value Estimator',
      'Market Rate Updates Dashboard',
      'Prioritisation by material value',
      'Priority Material Queue',
      'Collection status tracking',
      'Material Inventory Tracking',
      'Collection efficiency metrics',
      'Top material sources in your area',
      'Weekly material trend analysis',
      'Collection Route Planning',
      'Environmental Impact Reports',
      'Export Material Data (Excel and JSON)',
      'Generate Collection Reports',
      'Unlimited System Administrators'
    ],
    color: '#27ae60'
  },
  {
    id: 'food_bank',
    name: 'Food Banks & Community Fridges',
    subtitle: 'Reducing Food Waste, Feeding Communities',
    icon: FaHandHoldingHeart,
    basePrice: 99,
    headlineFeatures: [
      'Real-time food donation alerts in your territory',
      'Allergen tracking & food safety compliance'
    ],
    generalBenefits: {
      intro: 'Rescue more food, help more people:',
      items: [
        'Instant alerts when food donations are posted nearby',
        'Reduce food waste in your community',
        'Streamlined collection management',
        'Build partnerships with local donors',
        'Track your social impact with data',
        'Ensure food safety compliance effortlessly'
      ]
    },
    dashboardFeatures: [
      'Real-time food alerts by territory',
      'Quantity threshold notifications',
      'Food safety compliance tools',
      'Collection management dashboard',
      'Allergen tracking & filtering',
      'Expiry monitoring & alerts',
      'Donation claim system',
      'Donor relationship tracking',
      'Collection route planning',
      'Impact reporting & statistics',
      'Export data (Excel and JSON)',
      'Generate compliance reports'
    ],
    color: '#e74c3c'
  },
  {
    id: 'charity_shop',
    name: 'Charity Shops',
    subtitle: 'Turning Donations Into Community Impact',
    icon: FaStore,
    basePrice: 99,
    headlineFeatures: [
      'Nationwide charity item alerts',
      'Quality-filtered donation matching'
    ],
    generalBenefits: {
      intro: 'Source better donations, faster:',
      items: [
        'Postcode-based alerts for nearby donations',
        'Filter items by condition before collection',
        'Reduce time spent sourcing quality items',
        'Build a reliable donation pipeline',
        'Track donation trends and volumes',
        'Demonstrate community impact with data'
      ]
    },
    dashboardFeatures: [
      'Item condition filtering (1-5 star)',
      'Postcode-based donation alerts',
      'Inventory management tools',
      'Donation tracking dashboard',
      'Quality assessment tools',
      'Category-based item filtering',
      'Collection scheduling',
      'Donor communication tools',
      'Impact & sustainability reports',
      'Trend analysis & forecasting',
      'Export data (Excel and JSON)',
      'Generate collection reports'
    ],
    color: '#9b59b6'
  }
];

const PricingCard = ({ plan, selectedDuration, onDurationChange, expanded, onToggleExpand }) => {
  const IconComponent = plan.icon;
  const currentDuration = DURATION_OPTIONS.find(d => d.id === selectedDuration);
  const discountedPrice = Math.round(plan.basePrice * (1 - currentDuration.discount / 100));
  const totalPrice = discountedPrice * currentDuration.months;

  const visibleFeatures = expanded ? plan.dashboardFeatures : plan.dashboardFeatures.slice(0, 6);
  const hasMoreFeatures = plan.dashboardFeatures.length > 6;

  return (
    <div className="pricing-card" style={{ '--plan-color': plan.color }}>
      <div className="pricing-card-header">
        <div className="plan-icon" style={{ backgroundColor: plan.color }}>
          <IconComponent />
        </div>
        <h2>{plan.name}</h2>
        <p className="plan-subtitle">{plan.subtitle}</p>
      </div>

      <div className="duration-tabs">
        {DURATION_OPTIONS.map((duration) => (
          <button
            key={duration.id}
            className={`duration-tab ${selectedDuration === duration.id ? 'active' : ''} ${duration.popular ? 'popular-duration' : ''}`}
            onClick={() => onDurationChange(duration.id)}
            style={selectedDuration === duration.id ? { backgroundColor: plan.color } : {}}
          >
            {duration.label}
            {duration.popular && <span className="popular-tag"><FaStar /> Most Popular</span>}
          </button>
        ))}
      </div>
      
      <div className="pricing-card-price">
        <div className="price-row">
          <span className="original-price">£{plan.basePrice}</span>
          <span className="discount-badge" style={{ backgroundColor: plan.color }}>
            {currentDuration.discount}% OFF
          </span>
        </div>
        <div className="discounted-price">
          <span className="currency">£</span>
          <span className="amount">{discountedPrice}</span>
          <span className="period">/month</span>
        </div>
        <div className="total-price">
          Total: £{totalPrice.toLocaleString()} for {currentDuration.months} months
        </div>
      </div>

      <div className="features-section">
        {plan.headlineFeatures && plan.headlineFeatures.length > 0 && (
          <div className="headline-features">
            {plan.headlineFeatures.map((feature, index) => (
              <div key={index} className="headline-item" style={{ borderColor: plan.color }}>
                <FaStar className="headline-icon" style={{ color: plan.color }} />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        )}

        <div className="feature-category">
          <h3 className="category-title general-title">GENERAL BENEFITS</h3>
          <p className="benefits-intro">{plan.generalBenefits.intro}</p>
          <ul className="pricing-features benefits-list">
            {plan.generalBenefits.items.map((benefit, index) => (
              <li key={index}>
                <FaCheck className="check-icon" style={{ color: plan.color }} />
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        <div className="feature-category">
          <h3 className="category-title dashboard-title">DASHBOARD FEATURES</h3>
          <ul className="pricing-features dashboard-list">
            {visibleFeatures.map((feature, index) => (
              <li key={index}>
                <FaCheck className="check-icon" style={{ color: plan.color }} />
                {feature}
              </li>
            ))}
          </ul>
          {hasMoreFeatures && (
            <button 
              className="read-more-btn"
              onClick={onToggleExpand}
              style={{ color: plan.color }}
            >
              {expanded ? (
                <>Show Less <FaChevronUp /></>
              ) : (
                <>Read More ({plan.dashboardFeatures.length - 6} more) <FaChevronDown /></>
              )}
            </button>
          )}
        </div>
      </div>

      <Link to="/signup?type=subscriber" className="pricing-cta" style={{ backgroundColor: plan.color }}>
        Get Started
      </Link>
    </div>
  );
};

const Pricing = () => {
  const [durations, setDurations] = useState({
    authority: '12months',
    private_company: '12months',
    recycling: '12months',
    food_bank: '12months',
    charity_shop: '12months'
  });

  const [expandedCards, setExpandedCards] = useState({
    authority: false,
    private_company: false,
    recycling: false,
    food_bank: false,
    charity_shop: false
  });

  const handleDurationChange = (planId, durationId) => {
    setDurations(prev => ({ ...prev, [planId]: durationId }));
  };

  const handleToggleExpand = (planId) => {
    setExpandedCards(prev => ({ ...prev, [planId]: !prev[planId] }));
  };

  return (
    <div className="pricing-page">
      <div className="pricing-hero">
        <h1>Corporate Subscription Plans</h1>
        <p>Powerful tools for waste management professionals</p>
      </div>

      <div className="pricing-container">
        <div className="pricing-grid">
          {PLANS.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              selectedDuration={durations[plan.id]}
              onDurationChange={(durationId) => handleDurationChange(plan.id, durationId)}
              expanded={expandedCards[plan.id]}
              onToggleExpand={() => handleToggleExpand(plan.id)}
            />
          ))}
        </div>

        <div className="pricing-info">
          <div className="info-card">
            <FaUsers className="info-icon" />
            <h3>Team Collaboration</h3>
            <p>All corporate plans include team member accounts. Add colleagues during signup or later from your account settings.</p>
          </div>
          <div className="info-card">
            <FaCheck className="info-icon" />
            <h3>Geographic Boundaries</h3>
            <p>Your dashboard automatically filters waste reports to your assigned council territories using official UK boundaries.</p>
          </div>
        </div>

        <div className="pricing-individual">
          <h2>Individual Users</h2>
          <p>Want to report waste and track your personal impact? Individual accounts are <strong>completely free</strong>.</p>
          <Link to="/signup" className="individual-cta">Sign Up Free</Link>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
